import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import map from 'lodash/map';
import range from 'lodash/range';
import reverse from 'lodash/reverse';
import { useNavigation } from 'react-day-picker';
import { ChevronLeft, ChevronRight, DropDownArrow } from '@yme/assets/icons';
import { Accordion } from '@yme/atoms/accordion';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { colorBlackTransparent300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { AccordionItem } from './AccordionItem';
import { DropdownLabelStyled, DropdownOverlayStyled, LeftNavigationButtonStyled, NavigationButtonStyled, } from './CaptionStyled';
import { DropdownContext } from './DropdownContext';
import { MonthsGrid } from './MonthsGrid';
import { DATE_FORMAT } from './constants';
export const Caption = memo(({ dropdownRangeFrom, dropdownRangeTo, withFullMonthName, isRightToLeft }) => {
    const dropdownTitleRef = useRef(null);
    const { currentMonth, goToMonth, nextMonth, previousMonth } = useNavigation();
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);
    const toggleDropdown = useCallback(() => {
        setIsDropdownOpened(!isDropdownOpened);
    }, [isDropdownOpened]);
    const dropdownContextValue = useMemo(() => ({
        toggleDropdown,
    }), [toggleDropdown]);
    const handleGoToPrevMonth = useCallback(() => {
        goToMonth(previousMonth);
    }, [goToMonth, previousMonth]);
    const handleGoToNextMonth = useCallback(() => {
        goToMonth(nextMonth);
    }, [goToMonth, nextMonth]);
    const accordionData = useMemo(() => map(reverse(range(dropdownRangeFrom, dropdownRangeTo + 1)), (year) => ({
        isRightToLeft,
        title: String(year),
        children: React.createElement(MonthsGrid, { year: year }),
    })), [dropdownRangeFrom, dropdownRangeTo, isRightToLeft]);
    const strokeColor = isDropdownOpened ? colorBlackTransparent300 : undefined;
    const dateFormat = withFullMonthName ? DATE_FORMAT['MMMM y'] : DATE_FORMAT['MMM y'];
    const [month, year] = capitalize(format(currentMonth, dateFormat)).split('.');
    return (React.createElement(FlexWrapper, { justifyContent: "space-between", mb: "10px", pl: "6px" },
        React.createElement(DropdownContext.Provider, { value: dropdownContextValue },
            React.createElement("div", { ref: dropdownTitleRef },
                React.createElement(FlexWrapper, { padding: "2px", alignItems: "center", onClick: toggleDropdown },
                    React.createElement(DropdownLabelStyled, null,
                        React.createElement("span", null, isRightToLeft ? `\u00A0.${month}` : `${month}.\u00A0`),
                        React.createElement("span", null, year)),
                    React.createElement(SVGIcon, { type: DropDownArrow, iconRotate: isDropdownOpened ? 0 : 180, size: ICON_SIZE.s })),
                isDropdownOpened && (React.createElement(DropdownOverlayStyled, { dropdownTitleRef: dropdownTitleRef, isRightToLeft: isRightToLeft },
                    React.createElement(Accordion, { ItemComponent: AccordionItem, isRightToLeft: isRightToLeft, data: accordionData, hasContainerPadding: false, isOneItemOpen: true }))))),
        React.createElement(FlexWrapper, { isRightToLeft: isRightToLeft, flexDirection: isRightToLeft ? 'row-reverse' : 'row', alignItems: "center" },
            React.createElement(LeftNavigationButtonStyled, { onClick: handleGoToPrevMonth, isDisabled: isDropdownOpened },
                React.createElement(SVGIcon, { size: ICON_SIZE.xxs, type: ChevronLeft, stroke: strokeColor })),
            React.createElement(NavigationButtonStyled, { onClick: handleGoToNextMonth, isDisabled: isDropdownOpened },
                React.createElement(SVGIcon, { size: ICON_SIZE.xxs, type: ChevronRight, stroke: strokeColor })))));
});
Caption.displayName = 'Caption';
